import { useState, useCallback } from 'react';
import axios from '../../helpers/axios';
import {
  HitScopeClip,
  HitScopeProps,
  FetchClipRequest,
  ToastData,
} from './hitScope.types';
import { DateTime } from 'luxon';
import { formatHalfInningNum } from '../../helpers/utils';
import { strogging } from '@shd/jslib/infra';

const useHitScope = ({ type }: HitScopeProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>();
  const [clips, setClips] = useState<HitScopeClip[]>([]);
  const [m3u8Text, setM3u8Text] = useState<string | undefined>();
  const [selectedClip, setSelectedClip] = useState<HitScopeClip | undefined>(
    undefined
  );
  const [downloadStatus, setDownloadStatus] = useState<ToastData | undefined>();

  const formatOpponentName = useCallback(
    (clip: HitScopeClip) => {
      if (type === 'player') {
        return `vs ${clip?.textDescription?.split(' vs ')[1]}`;
      }
      return `${clip.teamNameMed[0]} @ ${clip.teamNameMed[1]}`;
    },
    [type]
  );

  const fetchUrl = useCallback(
    (payload: FetchClipRequest, download = false) => {
      const base = '/api/hitscope';
      const dlParam = download ? 'download=1&' : '';
      let req = '';
      if (type === 'player') {
        const { playerId, endAtTs, good, qType } = payload;
        req = `${base}/${playerId}?${dlParam}ts=${endAtTs}&good=${good}&qtype=${qType}`;
      } else if (type === 'game') {
        const { gameId, us, them } = payload;
        req = `${base}?${dlParam}game_id=${gameId}&us=${us}&them=${them}`;
      } else {
        const { teamIds, endAtTs, good, qType } = payload;
        req = `${base}?${dlParam}team_ids=${teamIds}&ts=${endAtTs}&good=${good}&qtype=${qType}`;
      }
      return encodeURI(req);
    },
    [type]
  );

  const fetchClips = useCallback(
    async (payload: FetchClipRequest) => {
      const url = fetchUrl(payload);
      setLoading(true);
      await axios
        .get(url)
        .then((response) => {
          const { data } = response;
          const transformedClips = data.clips.map((clip: HitScopeClip) => {
            const columnTwo =
              type !== 'game'
                ? { opponent: formatOpponentName(clip) }
                : { inning: formatHalfInningNum(clip.inningNum) };
            return {
              id: clip.id,
              date: DateTime.fromMillis(clip.clipStartTs * 1000).toLocal(),
              ...columnTwo,
              outcome: clip.textDescriptionBrief,
              more: 'More',
              startTs: clip.startTs,
              endTs: clip.endTs,
              fileDownloadUrl: clip.fileDownloadUrl,
              playerId: payload.playerId,
            };
          });
          setClips(transformedClips);
          if (response.data.clips.length > 0) {
            setSelectedClip(response.data.clips[0]);
          }
          setM3u8Text(data.m3u8_text);
          setLoading(false);
        })
        .catch((err) => {
          if (err.response && err.response.data) {
            strogging.log('HitScope fetchClips error', err.response.data);
            setError(err.response.data.message);
          } else {
            setError('Something went wrong. Please try again.');
          }
        });
    },
    [type, fetchUrl, formatOpponentName]
  );

  const downloadHitScopeClips = useCallback(
    async (payload: FetchClipRequest, numberOfClips: number) => {
      setDownloadStatus({
        label: `We’re preparing your video from ${numberOfClips} at-bat${
          numberOfClips > 1 ? 's' : ''
        }.
      This may take a bit - please keep this page open!`,
        background: 'status-error',
        duration: 60 * 1000,
      });
      const url = fetchUrl(payload, true);
      await axios
        .get(url, {
          // responseType: 'blob',
          timeout: 600 * 1000,
        })
        .then((response) => {
          setDownloadStatus({
            label: 'Your HitScope video is ready!',
            url: response.data.url,
          });
        })
        .catch((err) => {
          strogging.log('HitScope downloadHitScopeClips error', err);
          setDownloadStatus({
            label: 'Uh oh something went wrong.',
            background: 'status-error',
          });
        });
    },
    [fetchUrl]
  );

  return {
    fetchClips,
    downloadHitScopeClips,
    clips,
    m3u8Text,
    selectedClip,
    downloadStatus,
    loading,
    error,
  };
};

export default useHitScope;
